import { fetchData } from '@orientaction/utils';
import { ConsultationData } from 'src/utils/consultationStorage';
import config from './config';

const url = config.ai_url;

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
  metadata?: any;
  queryType?: string;
}

export type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ConsultationDetails {
  id: string;
  beneficiaryId: string;
  consultationType: string;
  selectedQuery: {
    queryType: string;
    jobPosition: string;
  };
  beneficiaryInfo?: {
    firstname?: string;
    firstName?: string;
    lastname?: string;
    lastName?: string;
    birthDate?: string;
    gender?: string;
    diploma?: string;
    postalCode?: string;
    city?: string;
  };
  notes: string;
  synthesis: string;
  createdAt: string;
  updatedAt: string;
  synthesisCreatedAt: string;
  processingStatus: ProcessingStatus;
}

export const submitConsultation = async (body: ConsultationData, identityData: any, cvFile: File | null) => {
  // Create FormData to handle file upload
  const formData = new FormData();

  // Append the CV file if it exists
  if (cvFile) {
    formData.append('cvFile', cvFile);
  }

  // Append all other data as JSON string
  const consultationDataWithoutFile = {
    ...body,
    selectedQuery: body.selectedQuery,
  };

  formData.append('data', JSON.stringify(consultationDataWithoutFile));

  formData.append('identityData', JSON.stringify(identityData));

  // Get token from localStorage for authentication
  const token = localStorage.getItem('token') || '';

  // Create headers without Content-Type (let browser set it with boundary)
  const headers: any = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Use fetchData for multipart/form-data request
  return fetchData(`${url}/api/consultations/submit`, 'POST', formData, headers);
};

export const getConsultationsByBeneficiary = async (
  beneficiaryId: string
): Promise<ConsultationDetails[]> => {
  const token = localStorage.getItem('token') || '';

  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchData(`${url}/api/consultations/beneficiary/${beneficiaryId}`, 'GET', null, headers);
};

export const getConsultationDetails = async (
  consultationId: string
): Promise<ConsultationDetails> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetchData(`${url}/api/consultations/${consultationId}`, 'GET', null, headers);
};

export const getConsultationMessages = async (consultationId: string): Promise<Message[]> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetchData(`${url}/api/consultations/${consultationId}/messages`, 'GET', null, headers);
};

export const sendConsultationMessage = async (
  consultationId: string,
  message: string,
  isQueryType = false
): Promise<Message> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const body = JSON.stringify({ message, isQueryType });
  return fetchData(`${url}/api/consultations/${consultationId}/continue`, 'POST', body, headers);
};

export const getSynthesis = async (consultationId: string): Promise<Message> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetchData(`${url}/api/consultations/${consultationId}/synthesis`, 'GET', null, headers);
};

export const createSynthesis = async (consultationId: string, notes: string): Promise<Message> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const body = JSON.stringify({ notes });
  return fetchData(`${url}/api/consultations/${consultationId}/synthesis`, 'POST', body, headers);
};

export const validateSynthesis = async (
  consultationId: string,
  notes: string
): Promise<Message> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const body = JSON.stringify({ notes });
  return fetchData(
    `${url}/api/consultations/${consultationId}/synthesis/validate`,
    'POST',
    body,
    headers
  );
};

export const saveConsultationNotes = async (
  consultationId: string,
  notes: string
): Promise<Message> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const body = JSON.stringify({ notes });
  return fetchData(`${url}/api/consultations/${consultationId}/notes`, 'POST', body, headers);
};

export const downloadSynthesis = async (consultationId: string): Promise<void> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${url}/api/consultations/${consultationId}/synthesis/download`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error('Failed to download synthesis');
  }

  // Get the PDF as a blob
  const blob = await response.blob();

  // Create a download link and trigger the download
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `synthèse.pdf`;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

export const sendSynthesisByEmail = async (
  consultationId: string,
  subject: string,
  content: string
): Promise<any> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${url}/api/consultations/${consultationId}/synthesis/send-email`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ subject, content, isHtml: true }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to send synthesis by email');
  }

  return response.json();
};

// Chatbot interfaces and functions
export interface ChatbotMessage {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

export interface ChatbotResponse {
  chatbotId: number;
  response: string;
  timestamp: string;
  problemType: string;
}

export interface ChatbotSession {
  id: number;
  beneficiaryId: string;
  problemType: string;
  title?: string;
  status: 'active' | 'completed' | 'archived';
  totalSessionDuration?: number; // Cumulative duration in seconds
  createdAt: string;
  lastInteractionAt: string;
}

export const submitChatbot = async (
  beneficiaryId: string,
  problemType: 'emotional-support' | 'problem-solving' | 'situation-clarity'
): Promise<ChatbotResponse> => {
  const token = localStorage.getItem('userToken') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };

  const body = JSON.stringify({
    beneficiaryId,
    token,
    problemType,
  });

  return fetchData(`${url}/api/chatbot/submit`, 'POST', body, headers);
};

export const getChatbotById = async (chatbotId: number): Promise<ChatbotSession> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchData(`${url}/api/chatbot/${chatbotId}`, 'GET', null, headers);
};

export const getChatbotsByBeneficiary = async (
  beneficiaryId: string
): Promise<ChatbotSession[]> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchData(`${url}/api/chatbot/beneficiary/${beneficiaryId}`, 'GET', null, headers);
};

export const getChatbotMessages = async (chatbotId: number): Promise<Message[]> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchData(`${url}/api/chatbot/${chatbotId}/messages`, 'GET', null, headers);
};

export const continueChatbot = async (
  chatbotId: number,
  message: string
): Promise<{ chatbotId: number; response: string }> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = JSON.stringify({ message });

  return fetchData(`${url}/api/chatbot/${chatbotId}/continue`, 'POST', body, headers);
};

export const updateChatbotStatus = async (
  chatbotId: number,
  status: 'active' | 'completed' | 'archived'
): Promise<{ message: string }> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = JSON.stringify({ status });

  return fetchData(`${url}/api/chatbot/${chatbotId}/status`, 'PATCH', body, headers);
};

/**
 * Start chatbot session timer
 */
export const startChatbotSessionTimer = async (chatbotId: number): Promise<void> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetchData(`${url}/api/chatbot/${chatbotId}/session/start`, 'POST', null, headers);
};

/**
 * Update chatbot session duration
 * @param durationInSeconds - Duration to ADD (not the total)
 */
export const updateChatbotSessionDuration = async (
  chatbotId: number,
  durationInSeconds: number
): Promise<{ message: string; totalDuration: number }> => {
  const token = localStorage.getItem('token') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = JSON.stringify({ durationInSeconds });

  return fetchData(`${url}/api/chatbot/${chatbotId}/session/duration`, 'PATCH', body, headers);
};

/**
 * Create a review for a chatbot session
 */
export const createChatbotReview = async (
  chatbotId: number,
  note: number,
  comment: string
): Promise<{ success: boolean; reviewId: number }> => {
  const token = localStorage.getItem('userToken') || '';

  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = JSON.stringify({ note, comment });

  return fetchData(`${url}/api/chatbot/${chatbotId}/review`, 'POST', body, headers);
};

/**
 * Get all chatbot reviews with pagination
 */
export const getChatbotReviews = async (
  page = 0,
  limit = 10,
  startDate?: string,
  endDate?: string
): Promise<{
  data: any[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> => {
  const token = localStorage.getItem('userToken') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let queryParams = `page=${page}&limit=${limit}`;
  if (startDate && startDate !== '') {
    queryParams += `&startDate=${startDate}`;
  }
  if (endDate && endDate !== '') {
    queryParams += `&endDate=${endDate}`;
  }
  return fetchData(`${url}/api/chatbot/reviews?${queryParams}`, 'GET', null, headers);
};

export const checkChatbotReviewExists = async (chatbotId: number): Promise<boolean> => {
  const token = localStorage.getItem('userToken') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetchData(
      `${url}/api/chatbot/${chatbotId}/review/exists`,
      'GET',
      null,
      headers
    );
    return response.exists || false;
  } catch (error) {
    return false;
  }
};

export interface ChatbotStatistics {
  totalChatbots: number;
  averageInteractionTimeMinutes: number;
  averageRating: number;
}

export const getChatbotStatistics = async (
  startDate?: string,
  endDate?: string
): Promise<ChatbotStatistics> => {
  const token = localStorage.getItem('userToken') || '';
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let queryParams = '';
  if (startDate) {
    queryParams += `?startDate=${startDate}`;
  }
  if (endDate) {
    queryParams += startDate ? `&endDate=${endDate}` : `?endDate=${endDate}`;
  }

  return fetchData(`${url}/api/chatbot/statistics${queryParams}`, 'GET', null, headers);
};

// Test synthesis functions
export const checkTestSynthesis = async (
  responseTestId: string
): Promise<{ exists: boolean; synthesis: string | null }> => {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  const response = await fetchData(
    `${url}/api/synthesis/check?responseTestId=${responseTestId}`,
    'GET',
    undefined,
    headers
  );

  // 404 / network failure: treat as "no existing synthesis" so the CTA can still show
  if (!response || typeof response.exists !== 'boolean') {
    return { exists: false, synthesis: null };
  }

  return response;
};

export const generateTestSynthesis = async (
  userId: string,
  responseTestId: string,
  testName: string,
  identityData: any,
  responseTestData: any
): Promise<string> => {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  const body = JSON.stringify({
    userId,
    responseTestId,
    testName,
    identityData,
    responseTestData,
  });
  const response = await fetchData(`${url}/api/synthesis/generate`, 'POST', body, headers);
  return response.synthesis;
};
